import { useMutation, useQuery } from '@tanstack/react-query';
import { waitlistApi } from '@/api/waitlist';
import { queryKeys, queryClient } from '@/lib/react-query';
import { WaitlistCreateRequest, WaitlistUnsubscribeRequest } from '@/types/api';
import { useToast } from '@/hooks/use-toast';
import { ApiError } from '@/lib/api-client';

// Hook to add email to waitlist
export const useAddToWaitlist = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: WaitlistCreateRequest) => waitlistApi.create(data),
    onSuccess: (data) => {
      // Invalidate status check for this email
      queryClient.invalidateQueries({
        queryKey: queryKeys.waitlist.status(data.email)
      });
    },
    onError: (error: ApiError) => {
      // Handle specific error cases
      if (error.status === 409) {
        toast({
          title: "Already registered",
          description: "This email is already on the waitlist.",
          variant: "destructive",
        });
      } else if (error.status === 400) {
        toast({
          title: "Invalid email",
          description: "Please provide a valid email address.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: error.error || "Failed to join waitlist. Please try again.",
          variant: "destructive",
        });
      }
    },
  });
};

// Hook to check waitlist status
export const useCheckWaitlistStatus = (email: string, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.waitlist.status(email),
    queryFn: () => waitlistApi.checkStatus(email),
    enabled: enabled && !!email && email.includes('@'), // Only run if valid email
    retry: false, // Don't retry 400 errors
    staleTime: 30 * 1000, // Consider data stale after 30 seconds
  });
};

// Hook to unsubscribe from waitlist
export const useUnsubscribeFromWaitlist = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: WaitlistUnsubscribeRequest) => waitlistApi.unsubscribe(data),
    onSuccess: (data) => {
      // Invalidate status check for this email
      queryClient.invalidateQueries({
        queryKey: queryKeys.waitlist.status(data.email)
      });

      toast({
        title: "Unsubscribed",
        description: "You've been removed from the waitlist.",
      });
    },
    onError: (error: ApiError) => {
      if (error.status === 404) {
        toast({
          title: "Not found",
          description: "This email is not on the waitlist.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: error.error || "Failed to unsubscribe. Please try again.",
          variant: "destructive",
        });
      }
    },
  });
};
